import { useEffect, useRef, useCallback, useState } from 'react';
import { collection, onSnapshot, Timestamp, QuerySnapshot, DocumentData, addDoc, serverTimestamp, query, where, orderBy, limit, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// Track already-notified requirement IDs to prevent duplicates
const getNotifiedIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem('crevvi_notified_requirement_ids');
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // Ignore localStorage errors
  }
  return new Set();
};

const addNotifiedId = (id: string): void => {
  try {
    const ids = getNotifiedIds();
    ids.add(id);
    // Keep only last 100 IDs to prevent storage bloat
    const trimmedIds = Array.from(ids).slice(-100);
    localStorage.setItem('crevvi_notified_requirement_ids', JSON.stringify(trimmedIds));
  } catch {
    // Ignore localStorage errors
  }
};

const getLastSeenTimestamp = (): number => {
  try {
    const stored = localStorage.getItem('crevvi_requirements_last_seen');
    if (stored) {
      return parseInt(stored, 10);
    }
  } catch {
    // Ignore localStorage errors
  }
  return Date.now();
};

const setLastSeenTimestamp = (timestamp: number): void => {
  try {
    localStorage.setItem('crevvi_requirements_last_seen', timestamp.toString());
  } catch {
    // Ignore localStorage errors
  }
};

// Helper to extract timestamp from Firestore Timestamp, Date, string, or number
const getRequirementTimestamp = (createdAt: Timestamp | Date | string | number | undefined): number => {
  if (!createdAt) return 0;
  
  // Firestore Timestamp
  if (typeof createdAt === 'object' && 'toDate' in createdAt && typeof createdAt.toDate === 'function') {
    return createdAt.toDate().getTime();
  }
  
  // Date object
  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }
  
  // String (ISO date or timestamp)
  if (typeof createdAt === 'string') {
    const parsed = Date.parse(createdAt);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  // Number (timestamp)
  if (typeof createdAt === 'number') {
    return createdAt;
  }
  
  return 0;
};

interface RequirementData {
  id: string;
  category?: string;
  title?: string;
  createdAt?: Timestamp | Date | string | number;
  [key: string]: any;
}

/**
 * Hook to listen for new requirements and notify users when they match selected categories.
 * 
 * Features:
 * - Listens to /requirements collection in real-time
 * - Filters for documents created after lastSeenTimestamp
 * - Checks if requirement.category is in user's selected categories
 * - Triggers browser notification (or in-app toast if permission denied)
 * - Prevents duplicate notifications using ID tracking
 * - Updates lastSeenTimestamp after processing
 */
export function useRequirementListener() {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { showToast } = useToast();
  
  // Track last seen timestamp locally
  const lastSeenRef = useRef<number>(getLastSeenTimestamp());
  const notifiedIdsRef = useRef<Set<string>>(getNotifiedIds());
  
  // Track notification permission
  const notificationPermissionRef = useRef<NotificationPermission>('default');

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      notificationPermissionRef.current = Notification.permission;
    }
  }, []);

  // Create in-app notification in Firestore
  const createInAppNotification = useCallback(
    async (requirement: RequirementData) => {
      if (!user) return;

      try {
        const category = requirement.category || 'Uncategorized';
        const notificationData = {
          recipientId: user.uid,
          title: 'New Requirement Match',
          message: `${category}: ${requirement.title || 'New job posted'}`,
          type: 'requirement_match',
          read: false,
          relatedId: requirement.id,
          relatedType: 'requirement',
          category: category,
          createdAt: serverTimestamp(),
          createdAtMs: Date.now(),
        };

        await addDoc(collection(db, 'notifications'), notificationData);
        console.log('[RequirementListener] Created in-app notification for:', requirement.id);
      } catch (err) {
        console.error('[RequirementListener] Error creating in-app notification:', err);
        // Don't block other notifications if this fails
      }
    },
    [user]
  );

  // Show notification helper
  const showNotification = useCallback(
    async (requirement: RequirementData) => {
      const title = 'New Requirement Match';
      const category = requirement.category || 'Uncategorized';
      const body = `${category}: ${requirement.title || 'New job posted'}`;

      // Create in-app notification (always, so it appears in /notifications page)
      await createInAppNotification(requirement);

      // Try browser notification first
      if (
        preferences.notificationsEnabled &&
        'Notification' in window &&
        notificationPermissionRef.current === 'granted'
      ) {
        try {
          const notification = new Notification(title, {
            body,
            icon: '/crevvi_icon.png',
            tag: requirement.id, // Prevents duplicate system notifications
            requireInteraction: false,
          });

          notification.onclick = () => {
            window.focus();
            window.location.href = `/requirements/${requirement.id}`;
            notification.close();
          };
        } catch (err) {
          console.error('Error showing notification:', err);
          // Fallback to in-app toast
          showToast(body, 'info', { title });
        }
      } else {
        // In-app toast fallback
        showToast(body, 'info', { title });
      }
    },
    [preferences.notificationsEnabled, showToast, createInAppNotification]
  );

  // Track if this is the first snapshot to handle initial load differently
  const isFirstSnapshotRef = useRef<boolean>(true);

  // Main listener effect
  useEffect(() => {
    // Only listen if user is logged in and has enabled notifications with categories selected
    if (!user) {
      console.log('[RequirementListener] No user logged in, skipping listener');
      return;
    }
    if (!preferences.notificationsEnabled) {
      console.log('[RequirementListener] Notifications disabled, skipping listener');
      return;
    }
    if (preferences.categories.length === 0) {
      console.log('[RequirementListener] No categories selected, skipping listener');
      return;
    }

    console.log('[RequirementListener] Starting listener with categories:', preferences.categories);
    console.log('[RequirementListener] Notifications enabled:', preferences.notificationsEnabled);
    console.log('[RequirementListener] Last seen timestamp:', lastSeenRef.current);

    // Build simple query - avoid any complex filters that might need indexes
    const requirementsRef = collection(db, 'requirements');
    
    // Use the simplest possible query - no orderBy to avoid index requirements
    // We'll sort and filter in memory
    console.log('[RequirementListener] Setting up onSnapshot listener...');
    
    const unsubscribe = onSnapshot(
      requirementsRef,
      { includeMetadataChanges: false },
      (snapshot: QuerySnapshot<DocumentData>) => {
        const now = Date.now();
        const matchedRequirements: RequirementData[] = [];
        
        console.log(`[RequirementListener] Received ${snapshot.size} requirements, ${snapshot.docChanges().length} changes`);
        console.log(`[RequirementListener] Is first snapshot: ${isFirstSnapshotRef.current}`);

        snapshot.docChanges().forEach((change) => {
          // Only process added documents (not modified or removed)
          if (change.type !== 'added') {
            console.log(`[RequirementListener] Skipping ${change.type} change for ${change.doc.id}`);
            return;
          }

          const data = change.doc.data();
          const requirement: RequirementData = {
            id: change.doc.id,
            ...data,
          };
          
          console.log(`[RequirementListener] Processing requirement: ${requirement.id}, category: ${requirement.category}, createdAt: ${requirement.createdAt}`);

          // Skip if already notified
          if (notifiedIdsRef.current.has(requirement.id)) {
            console.log(`[RequirementListener] Already notified for ${requirement.id}, skipping`);
            return;
          }

          // Check if requirement is newer than last seen
          const reqTimestamp = getRequirementTimestamp(requirement.createdAt);
          
          // On first snapshot (initial load), show notifications for requirements from last 30 days
          // On subsequent snapshots, only show for new requirements after lastSeen
          const isRecent = (now - reqTimestamp) < 30 * 24 * 60 * 60 * 1000; // 30 days
          
          if (!isFirstSnapshotRef.current && reqTimestamp <= lastSeenRef.current) {
            console.log(`[RequirementListener] Requirement ${requirement.id} is older than last seen (${reqTimestamp} <= ${lastSeenRef.current}), skipping`);
            return;
          }
          
          if (isFirstSnapshotRef.current && !isRecent) {
            console.log(`[RequirementListener] Requirement ${requirement.id} is not recent (older than 30 days), skipping on initial load`);
            return;
          }

          // Check if category matches user's selected categories (case-insensitive)
          const category = requirement.category?.toString().trim();
          if (!category) {
            console.log(`[RequirementListener] Requirement ${requirement.id} has no category, skipping`);
            return;
          }
          
          // Check for exact match or partial match
          const isMatch = preferences.categories.some(
            (userCat) => userCat.toLowerCase() === category.toLowerCase()
          );
          
          console.log(`[RequirementListener] Category match check: ${category} vs ${preferences.categories} = ${isMatch}`);
          
          if (isMatch) {
            console.log(`[RequirementListener] MATCH FOUND: ${requirement.id} - ${category}`);
            matchedRequirements.push(requirement);
            addNotifiedId(requirement.id);
            notifiedIdsRef.current.add(requirement.id);
          }
        });

        // Mark first snapshot as processed
        if (isFirstSnapshotRef.current) {
          isFirstSnapshotRef.current = false;
          console.log('[RequirementListener] First snapshot processed, switching to real-time mode');
        }

        // Show notifications for matched requirements
        if (matchedRequirements.length > 0) {
          console.log(`[RequirementListener] Showing notifications for ${matchedRequirements.length} matched requirements`);
          // For multiple matches, batch them or show individually
          if (matchedRequirements.length === 1) {
            showNotification(matchedRequirements[0]);
          } else {
            // Show summary notification
            const categories = [
              ...new Set(matchedRequirements.map((r) => r.category)),
            ];
            showToast(
              `${matchedRequirements.length} new requirements match your interests in: ${categories.join(', ')}`,
              'info',
              { title: 'New Requirement Matches' }
            );

            // Also show individual notifications if browser notifications are enabled
            if (
              'Notification' in window &&
              notificationPermissionRef.current === 'granted'
            ) {
              matchedRequirements.forEach((req, index) => {
                setTimeout(() => showNotification(req), index * 500);
              });
            }
          }
        } else {
          console.log('[RequirementListener] No matching requirements found in this batch');
        }

        // Update last seen timestamp
        if (snapshot.size > 0) {
          lastSeenRef.current = now;
          setLastSeenTimestamp(now);
          console.log(`[RequirementListener] Updated last seen timestamp to ${now}`);
        }
      },
      (error) => {
        console.error('[RequirementListener] Error in requirements listener:', error);
        // Silently handle permission errors - user might not have access to requirements
        if ((error as any)?.code === 'permission-denied') {
          console.log('[RequirementListener] Permission denied - requirements collection may have restricted access');
        } else {
          showToast('Failed to listen for new requirements', 'error');
        }
      }
    );

    return () => {
      console.log('[RequirementListener] Cleaning up listener');
      unsubscribe();
    };
  }, [
    user,
    preferences.notificationsEnabled,
    preferences.categories,
    showNotification,
    showToast,
  ]);

  // Return nothing - this is a side-effect only hook
  return null;
}

// Hook to get requirement match notifications for the notifications page
export function useRequirementMatchNotifications() {
  const { user } = useAuth();
  const [matchNotifications, setMatchNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('recipientId', '==', user.uid),
      where('type', '==', 'requirement_match'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const notifications = snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMatchNotifications(notifications);
    });

    return () => unsubscribe();
  }, [user]);

  return matchNotifications;
}

/**
 * Component wrapper for the requirement listener.
 * Use this in your app component tree to activate the listener.
 */
export function RequirementListener(): null {
  useRequirementListener();
  return null;
}

export default useRequirementListener;
